import { z } from 'zod';
import { agentSessionTransferSchema } from '../agent-session-transfer';
import { crowdexCollaborationSchema } from '../request-schemas';
import { crowdexSessionEventDataSchema } from '../ui-schemas';
import {
  localConversationAgentSchema,
  localConversationMessageSchema,
} from './local-conversations';

/**
 * Handing a conversation to another placement, on the session's server or on
 * Crowdex Cloud. One server owns a conversation at a time, as Crowdex Cloud's
 * lease registry records it. The page asks the server of its session
 * (`POST /api/handoffs`); that server moves the conversation between itself and
 * the cloud with the calls both answer (`/api/handoff/v2/*`), carrying it as a
 * `crowdex-handoff/v4` package.
 */

const providerIdentifier = z.string().regex(/^[a-z0-9][a-z0-9._-]*$/);
const commitSha = z.string().regex(/^[0-9a-f]{40}$/i);

export const handoffPullRequestSchema = z.object({
  url: z.string().url(),
  number: z.number().int().positive(),
  title: z.string().min(1).max(500),
  body: z.string().max(20_000),
  state: z.enum(['draft', 'open', 'merged', 'closed']),
  baseBranch: z.string().max(255),
  headBranch: z.string().max(255),
  updatedAt: z.string().datetime().nullable(),
});

/** How a server captures one of its folders for a handoff. */
export const handoffCheckoutSchema = z.discriminatedUnion('type', [
  /** The working tree as it is, as a patch against `baseRef`, optionally published on `branch`. */
  z.object({
    type: z.literal('snapshot'),
    baseRef: z.string().trim().min(1).max(255),
    branch: z.string().trim().min(1).max(255).optional(),
  }),
  /** A branch of the remote repository instead of the folder. */
  z.object({ type: z.literal('remote'), ref: z.string().trim().min(1).max(255) }),
]);

/** A repository on a code forge, by its host and full name (`github.com`, `acme/app`). */
export const handoffRepositorySchema = z.object({
  host: z.string().trim().min(1).max(253),
  fullName: z.string().min(1).max(512),
});

/** Where the conversation continues on the target server. */
export const handoffPlacementSchema = z.discriminatedUnion('kind', [
  /**
   * A folder of the target server's machine. A conversation coming from another server never
   * changes the user's folder: it continues in a worktree of the repository at `workspaceRoot`,
   * on `branch` (or one named after it).
   */
  z.object({
    kind: z.enum(['local', 'worktree']),
    workspaceRoot: z.string().min(1).max(4096),
    workspaceName: z.string().trim().min(1).max(200),
    branch: z.string().trim().min(1).max(255).optional(),
  }),
  z.object({
    kind: z.literal('remote'),
    providerId: providerIdentifier.optional(),
    profileId: providerIdentifier.optional(),
    /** The repository the remote workspace checks out, on the target server's source control. */
    repository: handoffRepositorySchema.optional(),
    pullRequest: handoffPullRequestSchema.nullable().optional(),
  }),
]);

const handoffAgentSchema = z.union([
  localConversationAgentSchema,
  z.object({ id: z.string().min(1).max(128), name: z.string().min(1).max(200) }),
]);

/** What the handoff records on the transcript and who continues it. */
const handoffContinuationSchema = z.object({
  /**
   * The agent that continues the conversation; a folder target needs its
   * launch command. A move between remote providers keeps the conversation's agent.
   */
  agent: handoffAgentSchema.optional(),
  modelId: z.string().trim().min(1).max(500).nullable().optional(),
  /** The session event the handoff records (agent, environment, model changes). */
  changes: crowdexSessionEventDataSchema.shape.changes.max(8).default([]),
});

export const conversationHandoffRequestSchema = handoffContinuationSchema.extend({
  conversationId: z.uuid(),
  target: z.object({
    /** Whether the conversation continues on Crowdex Cloud rather than the session's server. */
    cloud: z.boolean().default(false),
    placement: handoffPlacementSchema,
  }),
  /** How the owner captures the conversation's folder, when it runs in one. */
  checkout: handoffCheckoutSchema.optional(),
});

export const conversationHandoffResultSchema = z.object({
  conversationId: z.uuid(),
  /** Whether Crowdex Cloud owns the conversation now, rather than the session's server. */
  cloud: z.boolean(),
  runtime: z.enum(['local', 'worktree', 'cloud']),
  workspaceId: z.string(),
  logicalWorkspaceId: z.string(),
  workspaceRoot: z.string(),
  branch: z.string().nullable(),
  messages: z.array(localConversationMessageSchema),
  messageRevision: z.number().int().min(0),
  /** Whether the agent's own session moved with the conversation (it resumes natively). */
  agentSessionMoved: z.boolean(),
  providerId: providerIdentifier.nullable(),
  profileId: providerIdentifier.nullable(),
});

/**
 * The workspace a conversation carries between servers: its latest checkpoint (a Git patch on a
 * commit of its cloud project's repository, where the machine pushed it or the cloud has it; or
 * an archive of a folder without Git), or a branch of the remote repository.
 */
export const handoffWorkspaceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('patch'),
    /** The cloud project whose repository holds the base (`refs/crowdex/bases/<conversation>`). */
    projectId: z.uuid(),
    baseRef: z.string().min(1).max(255),
    baseCommitSha: commitSha,
    branch: z.string().min(1).max(255).nullable(),
  }),
  z.object({ kind: z.literal('archive') }),
  z.object({ kind: z.literal('ref'), ref: z.string().min(1).max(255) }),
]);

export const HANDOFF_FORMAT = 'crowdex-handoff/v4';

/** The entries a package may carry after its manifest, in this order. */
export const HANDOFF_ENTRIES = [
  'transcript.json',
  'workspace.patch',
  'workspace.archive',
  'agent-session.json',
  'storage.json',
  'triggers.json',
  'subscriptions.json',
  'attachments.json',
] as const;
export type HandoffEntryName = (typeof HANDOFF_ENTRIES)[number];

/** A package's first entry (`manifest.json`): the conversation, and the entries that follow. */
export const handoffManifestSchema = z.object({
  format: z.literal(HANDOFF_FORMAT),
  conversationId: z.uuid(),
  title: z.string(),
  collaboration: crowdexCollaborationSchema,
  modelId: z.string().nullable(),
  logicalWorkspaceId: z.string().nullable(),
  /** The name of the folder the workspace is, which names its checkout where no repository does. */
  workspaceName: z.string().max(200).nullable(),
  repository: handoffRepositorySchema.nullable(),
  /** The agent that ran it, continued by default when the page names none. */
  agent: z.object({ id: z.string().min(1).max(128), name: z.string().min(1).max(200) }).nullable(),
  workspace: handoffWorkspaceSchema,
  entries: z.array(z.enum(HANDOFF_ENTRIES)).max(HANDOFF_ENTRIES.length),
});

/** `transcript.json`. */
export const handoffTranscriptSchema = z.array(localConversationMessageSchema).max(10_000);

/** `agent-session.json`: the native agent's session, resumed natively at the new owner. */
export const handoffAgentSessionSchema = agentSessionTransferSchema;

/** `storage.json`: the plugins' documents of the conversation and of its workspace. */
export const handoffStorageSchema = z
  .array(
    z.object({
      plugin: z.string().min(1).max(128),
      scope: z.literal('workspace'),
      collection: z.string().min(1).max(128),
      id: z.string().min(1).max(256),
      value: z.unknown(),
      indexes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
    }),
  )
  .max(100_000);

/**
 * `attachments.json`: the message attachments the transcript refers to
 * (`/api/drive/content?conversation=<id>&attachment=<id>`), from the internal store, stored
 * again at the new owner under the same keys (`docs/adr-two-file-spaces.md`, decision 6). Only
 * present when the transcript refers to some. An attachment's id is the digest of its name,
 * media type and content, checked on import.
 */
export const handoffAttachmentsSchema = z
  .array(
    z.object({
      conversationId: z.string().regex(/^[A-Za-z0-9-]{1,128}$/),
      id: z.string().regex(/^[0-9a-f]{32}$/),
      name: z.string().min(1).max(255),
      mediaType: z.string().min(1).max(255),
      /** The content, in base64. */
      content: z.base64(),
    }),
  )
  .max(10_000);

/**
 * `triggers.json`: the plugins' timers and webhooks set in the conversation, registered
 * again at the new owner. A webhook keeps its token, so its public URL does not change: the
 * cloud serves it, or relays it to a linked machine (`docs/adr-webhook-relay.md`).
 */
export const handoffTriggersSchema = z
  .array(
    z.object({
      plugin: z.string().min(1).max(128),
      triggerId: z.string().min(1).max(128),
      kind: z.enum(['timer', 'url']),
      at: z.string().nullable(),
      /** A webhook's secret path segment. */
      token: z.string().min(1).max(128).nullable().optional(),
    }),
  )
  .max(10_000);

/**
 * `subscriptions.json`: what wakes the conversation (its agent's event subscriptions) and the
 * events still waiting to wake it, carried to the new owner.
 */
export const handoffSubscriptionsSchema = z
  .array(
    z.object({
      types: z.array(z.string().min(1).max(200)).min(1).max(16),
      filter: z.record(
        z.string().max(64),
        z.union([
          z.string(),
          z.number(),
          z.boolean(),
          z.array(z.union([z.string(), z.number(), z.boolean()])),
        ]),
      ),
      reason: z.string().max(500).nullable(),
      once: z.boolean(),
      /** It watches work the conversation started; older servers send none. */
      ownWork: z.boolean().default(false),
      expiresAt: z.string().nullable(),
      waiting: z.array(z.record(z.string(), z.unknown())).max(50),
    }),
  )
  .max(50);
export type HandoffSubscriptions = z.infer<typeof handoffSubscriptionsSchema>;

/** `POST /api/handoff/v2/export`: the owner, stopped, streams the conversation's package. */
export const handoffExportRequestSchema = z.object({
  conversationId: z.uuid(),
  checkout: handoffCheckoutSchema.optional(),
  /** The repository the cloud workspace checks out, when the target placement names one. */
  repository: handoffRepositorySchema.nullable().optional(),
});

/**
 * `POST /api/handoff/v2/import`: the target takes a package (the request body) as a pending
 * conversation; its continuation is in the `x-crowdex-handoff` header (JSON).
 */
export const handoffImportParamsSchema = handoffContinuationSchema.extend({
  placement: handoffPlacementSchema,
});

/** `/api/handoff/v2/{stop,resume,activate,discard,release}`: one conversation. */
export const handoffConversationRequestSchema = z.object({ conversationId: z.uuid() });

/** The import result, before the session's server says which server it is. */
export const handoffImportResultSchema = conversationHandoffResultSchema.omit({ cloud: true });

/** An owner in Crowdex Cloud's lease registry: the cloud, or one of the user's machines. */
export const handoffOwnerSchema = z.union([
  z.literal('cloud'),
  z.string().regex(/^machine:[0-9a-f-]{36}$/),
]);

/** `POST /api/handoff/v2/lease/begin`: a move of the conversation from its owner to `to`. */
export const handoffLeaseBeginSchema = z.object({
  conversationId: z.uuid(),
  from: handoffOwnerSchema,
  to: handoffOwnerSchema,
});
export const handoffLeaseSchema = z.object({
  conversationId: z.uuid(),
  owner: handoffOwnerSchema,
  epoch: z.number().int().min(0),
  /** The move in progress, if any. */
  moving: z.object({ to: handoffOwnerSchema, nonce: z.uuid(), since: z.string() }).nullable(),
});
/** `POST /api/handoff/v2/lease/{flip,abort}`: ends the move `nonce` names. */
export const handoffLeaseMoveSchema = z.object({ conversationId: z.uuid(), nonce: z.uuid() });

export type HandoffCheckout = z.infer<typeof handoffCheckoutSchema>;
export type HandoffPlacement = z.infer<typeof handoffPlacementSchema>;
export type HandoffWorkspace = z.infer<typeof handoffWorkspaceSchema>;
export type HandoffManifest = z.infer<typeof handoffManifestSchema>;
export type HandoffStorage = z.infer<typeof handoffStorageSchema>;
export type HandoffTriggers = z.infer<typeof handoffTriggersSchema>;
export type HandoffImportParams = z.output<typeof handoffImportParamsSchema>;
export type HandoffOwner = z.infer<typeof handoffOwnerSchema>;
export type HandoffLease = z.infer<typeof handoffLeaseSchema>;
export type HandoffImportResult = z.infer<typeof handoffImportResultSchema>;
export type ConversationHandoffRequest = z.input<typeof conversationHandoffRequestSchema>;
export type ConversationHandoffResult = z.infer<typeof conversationHandoffResultSchema>;
